/**
 * Appels à l'API REST (US-01, US-06). Une fonction par route consommée par l'écran.
 */
import type {
  AddRecipeToShoppingRequest,
  ApercuSuppression,
  ApiErrorBody,
  CreateInvitationResponse,
  InvitationInfo,
  PublicInvitation,
  CreateShoppingItemRequest,
  CreateCookLogRequest,
  CreateCookLogResponse,
  CreateRecipeRequest,
  CreateRecipeResponse,
  GetRecipeResponse,
  ImportResponse,
  ImportPhotoRequest,
  ImportPhotoResponse,
  ImportTextRequest,
  ImportTextResponse,
  ImportUrlRequest,
  ImportUrlResponse,
  CreateShareLinkResponse,
  GetShareLinkResponse,
  HealthResponse,
  ListCollectionsResponse,
  ListJournalWithStatsResponse,
  ListMealPlanResponse,
  ListRecipesResponse,
  ListShoppingResponse,
  Perimetre,
  ApercuRegleResponse,
  CreateTagRuleRequest,
  ListTagRulesResponse,
  ListTagsResponse,
  RecipeCollectionsResponse,
  MealPlan,
  MealSlot,
  PlanToShoppingRequest,
  SearchByIngredientsResponse,
  SetMealPlanRequest,
  SharedRecipeResponse,
  ShoppingItem,
  UpdateIngredientsRequest,
  UpdateRecipeRequest,
  UpdateRecipeResponse,
  PorteeJournal,
  CookLogSocial,
  CreateCommentRequest,
  CreateSuggestionRequest,
  ListActivityResponse,
  ListSuggestionsResponse,
  ResolveSuggestionRequest,
  ReactionKind,
  ShareCookLogsRequest,
  UpdateReactionRequest,
  ShareCookLogsResponse,
  UpdateCookLogSharedRequest,
  UpdateCookLogSharedResponse,
  UpdateRecipeNoteRequest,
  UpdateRecipeNoteResponse,
  UpdateRecipeRangementRequest,
  UpdateRecipeRangementResponse,
  RecipeStatus,
  UpdateRecipeTagsRequest,
  UpdateRecipeTagsResponse,
  UpdateShoppingItemRequest,
  UpdateStepIngredientsRequest,
  UpdateStepsRequest,
  StepOrderInput,
  UploadCookLogPhotoResponse,
  UploadPhotoResponse,
  UpsertCollectionRequest,
  UpsertCollectionResponse,
  TagRuleCriteres,
  UpdateTagRuleRequest,
  UpdateTagRuleResponse,
  UpsertTagRequest,
  UpsertTagResponse,
  MemberRole,
  RecipeVisibility,
  PublicMember,
  SessionState,
  CheckAiKeyResponse,
  GarderImportDraftRequest,
  GarderImportDraftResponse,
  InstanceSettingsResponse,
  ListImportDraftsResponse,
  MajPropositionsRequest,
  MajPropositionsResponse,
  UpdateInstanceSettingsRequest,
  CreateShortcutTokenRequest,
  CreateShortcutTokenResponse,
  ListShortcutTokensResponse,
} from '@mlc/shared';
import { enqueueMutation, type QueuedMethod } from './offlineQueue';

/**
 * Erreur porteuse du code stable renvoyé par le serveur (`ApiErrorBody.error.code`),
 * en plus du message lisible. Les écrans qui n'ont besoin que du message
 * continuent de fonctionner sans changement (`ApiError` reste une `Error`) ;
 * l'import (US-02) s'en sert pour distinguer « aucune recette structurée, pas
 * de clé IA » des autres échecs et proposer le collage manuel à ce moment précis.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Écriture mise en file faute de réseau (US-12, §1 CLAUDE.md : « les
 * écritures hors ligne sont mises en file et rejouées à la reconnexion »).
 * Distincte d'`ApiError` : ce n'est pas un échec, la donnée est acceptée et
 * en attente — un écran peut donc l'accueillir avec un message neutre plutôt
 * qu'une alerte d'erreur.
 */
export class OfflineQueuedError extends Error {
  constructor(readonly queuedId: string) {
    super('Hors connexion : l’enregistrement est mis en file, il sera synchronisé à la reconnexion.');
    this.name = 'OfflineQueuedError';
  }
}

/** Lève l'erreur applicative correspondant à une réponse en échec. Ne revient jamais. */
async function throwApiError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  // Session expirée (US-19) : signalée globalement plutôt que remontée écran
  // par écran — n'importe quelle requête peut recevoir un 401, et chaque
  // appelant n'a pas à savoir quoi en faire. `App` affiche la connexion.
  if (response.status === 401 && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mlc:unauthenticated'));
  }
  // Amorçage (US-42) : la base n'a aucun membre, il n'y a donc personne à qui
  // demander un mot de passe. Signalé globalement pour la même raison que le
  // 401 — n'importe quelle requête peut le recevoir au tout premier démarrage.
  if (response.status === 503 && body?.error.code === 'bootstrap_required' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mlc:bootstrap'));
  }
  throw new ApiError(body?.error.code ?? 'unknown', body?.error.message ?? `Erreur serveur (${response.status}).`);
}

async function readJsonOrThrow<T>(response: Response): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  return throwApiError(response);
}

/**
 * DELETE sans corps de réponse (204). À part des autres écritures : la file
 * hors ligne (US-12) ne rejoue que POST/PATCH/PUT, et une suppression mise en
 * file serait rejouée sur un objet qui n'existe peut-être plus au moment de la
 * reconnexion. Hors ligne, une suppression échoue franchement plutôt que de
 * promettre un effet différé.
 */
async function deleteOrThrow(url: string): Promise<void> {
  const response = await fetch(url, { method: 'DELETE' });
  if (!response.ok) await throwApiError(response);
}

/** `fetch` ne rejette que pour un échec réseau (hors ligne, DNS, CORS…), jamais pour un statut HTTP — c'est ce qui distingue « pas de réseau » d'une erreur applicative. */
function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError;
}

/**
 * POST/PATCH/PUT au corps JSON, avec repli sur la file hors ligne (US-12) en
 * cas d'échec réseau. Réservé aux écritures dont le corps est un objet
 * JSON-sérialisable ; les envois de photo (`multipart/form-data`) n'y
 * passent pas — cf. commentaire d'en-tête d'offlineQueue.ts.
 */
async function jsonMutation<T>(url: string, method: QueuedMethod, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    const entry = enqueueMutation(method, url, body);
    throw new OfflineQueuedError(entry.id);
  }
  return readJsonOrThrow<T>(response);
}

/**
 * Options de listage de la bibliothèque. Un objet plutôt qu'une suite de
 * paramètres positionnels : `listRecipes(q, signal, false, 'alpha', 'favorite',
 * ['été'])` ne se lit plus, et l'ordre des trois derniers ne s'invente pas.
 */
export interface ListRecipesOptions {
  signal?: AbortSignal;
  /** Liste les recettes archivées à la place de la bibliothèque. */
  archived?: boolean;
  sort?: string;
  /** Statut de rangement (US-21). `''` ou absent : aucun filtre de statut. */
  status?: string;
  /** Noms d'étiquettes, cumulés par ET (US-21). */
  tags?: string[];  /** Nature du plat (US-55). Une valeur, jamais une liste. */
  course?: string;
  /** Cuisine d'origine (US-55). */
  cuisine?: string;
  /**
   * Périmètre du fonds (US-97) : `livre` (défaut côté serveur), `fonds`,
   * `tout`. C'est l'écran qui le choisit — la bibliothèque, celui du fonds,
   * les archives — et jamais un filtre appliqué après coup sur ce qui est
   * arrivé.
   */
  perimetre?: Perimetre;
}

/**
 * GET /api/recipes — bibliothèque, recherche, tri et filtres. Tous les
 * critères passent par l'URL, jamais par un état local : c'est ce qui rend une
 * vue filtrée partageable et restituable au bouton retour (US-13).
 *
 * Les valeurs par défaut du serveur ne sont pas écrites dans l'URL — une
 * bibliothèque sans filtre reste `/api/recipes` tout court.
 */
export function listRecipes(query: string, options: ListRecipesOptions = {}): Promise<ListRecipesResponse> {
  const trimmed = query.trim();
  const params = new URLSearchParams();
  if (trimmed.length > 0) params.set('q', trimmed);
  if (options.archived === true) params.set('archived', '1');
  if (options.sort !== undefined && options.sort !== 'recent') params.set('tri', options.sort);
  if (options.status !== undefined && options.status !== '') params.set('statut', options.status);
  const tags = (options.tags ?? []).filter((name) => name.trim().length > 0);
  if (tags.length > 0) params.set('etiquettes', tags.join(','));
  // Nature du plat et cuisine (US-55) : une valeur chacune, pas une liste.
  if (options.course !== undefined && options.course !== '') params.set('type', options.course);
  if (options.cuisine !== undefined && options.cuisine !== '') params.set('cuisine', options.cuisine);
  // `livre` est le défaut du serveur : ne pas l'écrire garde `/api/recipes`
  // tout court pour la bibliothèque, comme les autres valeurs par défaut.
  if (options.perimetre !== undefined && options.perimetre !== 'livre') params.set('perimetre', options.perimetre);
  const search = params.toString();
  const url = search.length > 0 ? `/api/recipes?${search}` : '/api/recipes';
  return fetch(url, options.signal === undefined ? {} : { signal: options.signal })
    .then((response) => readJsonOrThrow<ListRecipesResponse>(response));
}

/** GET /api/ingredients — noms d'ingrédients déjà en bibliothèque, pour l'autocomplétion (US-29). */
export function listIngredientNames(signal?: AbortSignal): Promise<string[]> {
  return fetch('/api/ingredients', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<string[]>(response));
}

/**
 * GET /api/recipes/par-ingredients (US-29) — « qu'est-ce que je peux faire
 * avec ce que j'ai ». Classées par nombre d'ingrédients manquants croissant,
 * chacune disant ce qui lui manque.
 */
export function searchByIngredients(
  names: string[],
  signal?: AbortSignal,
  perimetre: Perimetre = 'livre',
): Promise<SearchByIngredientsResponse> {
  const kept = names.map((name) => name.trim()).filter((name) => name.length > 0);
  if (kept.length === 0) return Promise.resolve([]);
  const params = new URLSearchParams({ ingredients: kept.join(',') });
  // Le même périmètre que la liste qui l'entoure (US-97) : « avec ce que
  // j'ai » est un mode de la bibliothèque, pas un écran à part.
  if (perimetre !== 'livre') params.set('perimetre', perimetre);
  const url = `/api/recipes/par-ingredients?${params.toString()}`;
  return fetch(url, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<SearchByIngredientsResponse>(response));
}

// ---------------------------------------------------------------------------
// Partage d'une recette (US-33)
// ---------------------------------------------------------------------------

export function getShareLink(recipeId: string, signal?: AbortSignal): Promise<GetShareLinkResponse> {
  return fetch(`/api/recipes/${recipeId}/partage`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<GetShareLinkResponse>(response));
}

/** Crée le lien public, ou renvoie celui qui existe déjà — un seul lien actif par recette. */
export function createShareLink(recipeId: string): Promise<CreateShareLinkResponse> {
  return jsonMutation<CreateShareLinkResponse>(`/api/recipes/${recipeId}/partage`, 'POST', {});
}

/** Révoque le lien actif. Partager n'est pas publier : le geste doit être réversible. */
export function revokeShareLink(recipeId: string): Promise<void> {
  return deleteOrThrow(`/api/recipes/${recipeId}/partage`);
}

/** GET /api/partage/:token — la recette derrière un lien public. La seule route de l'API qui s'ouvre sans mot de passe. */
export function getSharedRecipe(token: string, signal?: AbortSignal): Promise<SharedRecipeResponse> {
  return fetch(`/api/partage/${encodeURIComponent(token)}`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<SharedRecipeResponse>(response));
}

// ---------------------------------------------------------------------------
// Journal transversal (US-30)
// ---------------------------------------------------------------------------

export interface ListJournalOptions {
  /** Recherche dans les remarques (« ce qui s'est passé », « la prochaine fois »). */
  query?: string;
  /** Note minimale, sous forme de chaîne : `''` vaut « toutes ». */
  minRating?: string;
  recipeId?: string;
  /** « Mon journal » (défaut) ou « la famille » (US-65). */
  portee?: PorteeJournal;
  signal?: AbortSignal;
}

export function listJournal(options: ListJournalOptions = {}): Promise<ListJournalWithStatsResponse> {
  const params = new URLSearchParams();
  if (options.query !== undefined && options.query.trim() !== '') params.set('q', options.query.trim());
  if (options.minRating !== undefined && options.minRating !== '') params.set('note', options.minRating);
  if (options.recipeId !== undefined && options.recipeId !== '') params.set('recette', options.recipeId);
  // Portée (US-65) : « mon journal » reste le défaut, et n'est donc jamais
  // envoyé — une portée par défaut écrite dans l'URL laisserait croire à un
  // choix qu'on n'a pas fait.
  if (options.portee === 'famille') params.set('portee', 'famille');
  const search = params.toString();
  return fetch(search === '' ? '/api/journal' : `/api/journal?${search}`, options.signal === undefined ? {} : { signal: options.signal })
    .then((response) => readJsonOrThrow<ListJournalWithStatsResponse>(response));
}

// ---------------------------------------------------------------------------
// Liste de courses (US-28)
// ---------------------------------------------------------------------------

export function listShopping(signal?: AbortSignal): Promise<ListShoppingResponse> {
  return fetch('/api/shopping', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListShoppingResponse>(response));
}

/**
 * Ajoute les ingrédients d'une recette à la liste, **aux portions choisies**.
 * Les quantités sont recalculées par le serveur à partir de la recette
 * enregistrée : le §3.1 s'applique de la même manière quel que soit l'écran
 * qui demande.
 */
export function addRecipeToShopping(recipeId: string, targetServings: number | null): Promise<ListShoppingResponse> {
  return jsonMutation<ListShoppingResponse>(
    `/api/shopping/from-recipe/${recipeId}`,
    'POST',
    { target_servings: targetServings } satisfies AddRecipeToShoppingRequest,
  );
}

export function createShoppingItem(body: CreateShoppingItemRequest): Promise<ShoppingItem> {
  return jsonMutation<ShoppingItem>('/api/shopping', 'POST', body);
}

export function updateShoppingItem(id: string, body: UpdateShoppingItemRequest): Promise<ShoppingItem> {
  return jsonMutation<ShoppingItem>(`/api/shopping/${id}`, 'PATCH', body);
}

export function deleteShoppingItem(id: string): Promise<void> {
  return deleteOrThrow(`/api/shopping/${id}`);
}

/** Vide ce qui est dans le panier, en une fois, au retour du magasin. */
export function clearCheckedShoppingItems(): Promise<void> {
  return deleteOrThrow('/api/shopping/checked');
}

// ---------------------------------------------------------------------------
// Planification hebdomadaire (US-36)
// ---------------------------------------------------------------------------

/** GET /api/plan?debut= — les sept jours à partir de ce lundi. */
export function listMealPlan(start: string, signal?: AbortSignal): Promise<ListMealPlanResponse> {
  return fetch(`/api/plan?debut=${encodeURIComponent(start)}`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListMealPlanResponse>(response));
}

/**
 * PUT /api/plan — pose ou remplace le plat d'un créneau. Passe par la file
 * hors ligne comme les autres écritures : décider ses repas de la semaine se
 * fait volontiers depuis le canapé, où le réseau vaut ce qu'il vaut.
 */
export function setMeal(body: SetMealPlanRequest): Promise<MealPlan> {
  return jsonMutation<MealPlan>('/api/plan', 'PUT', body);
}

/** DELETE /api/plan/:date/:meal — libère un créneau. Un plan se change, c'est même sa raison d'être. */
export function clearMeal(date: string, meal: MealSlot): Promise<void> {
  return deleteOrThrow(`/api/plan/${date}/${meal}`);
}

/**
 * POST /api/plan/courses — la boucle se referme : ce qu'on a décidé de manger
 * devient ce qu'il faut acheter, avec la même agrégation qu'une recette isolée
 * (US-28).
 */
export function planWeekToShopping(start: string): Promise<ListShoppingResponse> {
  return jsonMutation<ListShoppingResponse>('/api/plan/courses', 'POST', { start } satisfies PlanToShoppingRequest);
}

// ---------------------------------------------------------------------------
// Étiquettes manuelles et collections (US-21)
// ---------------------------------------------------------------------------

/** GET /api/tags — étiquettes manuelles et automatiques, avec le nombre de recettes non archivées qui les portent. */
export function listTags(signal?: AbortSignal): Promise<ListTagsResponse> {
  return fetch('/api/tags', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListTagsResponse>(response));
}

/** POST /api/tags. Réutilise l'étiquette existante si le nom est déjà pris — créer deux fois la même est le même geste. */
export function createTag(name: string): Promise<UpsertTagResponse> {
  return jsonMutation<UpsertTagResponse>('/api/tags', 'POST', { name } satisfies UpsertTagRequest);
}

export function renameTag(id: string, name: string): Promise<UpsertTagResponse> {
  return jsonMutation<UpsertTagResponse>(`/api/tags/${id}`, 'PATCH', { name } satisfies UpsertTagRequest);
}

/**
 * GET /api/regles-etiquette (US-61) — les règles des étiquettes calculées.
 * Lisible par tout membre (les libellés sont déjà partout à l'écran) ;
 * modifiable par le seul propriétaire.
 */
export function listTagRules(signal?: AbortSignal): Promise<ListTagRulesResponse> {
  return fetch('/api/regles-etiquette', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListTagRulesResponse>(response));
}

/** PATCH /api/regles-etiquette/:id — renommer, régler un seuil, allumer ou éteindre. Recalcule toute la bibliothèque. */
export function updateTagRule(id: string, patch: UpdateTagRuleRequest): Promise<UpdateTagRuleResponse> {
  return jsonMutation<UpdateTagRuleResponse>(`/api/regles-etiquette/${id}`, 'PATCH', patch);
}

/** POST /api/regles-etiquette (US-62) — composer une règle. Propriétaire uniquement. */
export function createTagRule(body: CreateTagRuleRequest): Promise<UpdateTagRuleResponse> {
  return jsonMutation<UpdateTagRuleResponse>('/api/regles-etiquette', 'POST', body);
}

/** DELETE /api/regles-etiquette/:id — seulement une règle sur mesure ; les quatre d'origine s'éteignent. */
export function deleteTagRule(id: string): Promise<void> {
  return deleteOrThrow(`/api/regles-etiquette/${id}`);
}

/** POST /api/regles-etiquette/apercu — « cette règle poserait l'étiquette sur 7 recettes », dans le périmètre visible. */
export function apercuTagRule(criteres: TagRuleCriteres): Promise<ApercuRegleResponse> {
  return jsonMutation<ApercuRegleResponse>('/api/regles-etiquette/apercu', 'POST', { criteres });
}

/** DELETE /api/tags/:id. Supprime un rangement, jamais une recette. Refusé sur une étiquette calculée. */
export function deleteTag(id: string): Promise<void> {
  return deleteOrThrow(`/api/tags/${id}`);
}

/**
 * PUT /api/recipes/:id/rangement (US-51) — le statut de rangement **de ce
 * membre**. Distinct de `updateRecipe` : ranger la Tropézienne de Claire est
 * permis, la modifier ne l'est pas.
 */
/**
 * PUT /api/recipes/:id/note (US-64) — la note **de ce membre**. Distincte
 * d'`updateRecipe` pour la même raison que le rangement : annoter la
 * Tropézienne de Claire est permis, la modifier ne l'est pas.
 */
export function updateRecipeNote(id: string, note: string | null): Promise<UpdateRecipeNoteResponse> {
  return jsonMutation<UpdateRecipeNoteResponse>(
    `/api/recipes/${id}/note`,
    'PUT',
    { note } satisfies UpdateRecipeNoteRequest,
  );
}

/**
 * PUT /api/recipes/:id/cook-log/:logId/partage (US-65) — publier une de ses
 * réalisations, ou la reprendre. Refusée sur la réalisation d'un autre et sur
 * une recette privée : 404, comme partout.
 */
export function updateCookLogShared(
  recipeId: string,
  logId: string,
  shared: boolean,
): Promise<UpdateCookLogSharedResponse> {
  return jsonMutation<UpdateCookLogSharedResponse>(
    `/api/recipes/${recipeId}/cook-log/${logId}/partage`,
    'PUT',
    { shared } satisfies UpdateCookLogSharedRequest,
  );
}

/** POST /api/realisations/:logId/commentaires (US-66) — on ne commente que ce qu'on voit ; 404 sinon. */
export function addComment(logId: string, body: string): Promise<CookLogSocial> {
  return jsonMutation<CookLogSocial>(
    `/api/realisations/${logId}/commentaires`,
    'POST',
    { body } satisfies CreateCommentRequest,
  );
}

/** DELETE /api/realisations/:logId/commentaires/:id — par l'auteur ou par le propriétaire de la recette. Suppression douce. */
export async function deleteComment(logId: string, commentId: string): Promise<CookLogSocial> {
  // `deleteOrThrow` ne rend rien : cette route-ci renvoie le fil à jour, pour
  // que l'écran n'ait pas à recharger la fiche entière.
  const response = await fetch(`/api/realisations/${logId}/commentaires/${commentId}`, { method: 'DELETE' });
  return await readJsonOrThrow<CookLogSocial>(response);
}

/** PUT /api/realisations/:logId/reaction — une par personne ; `null` retire la sienne. */
export function setReaction(logId: string, kind: ReactionKind | null): Promise<CookLogSocial> {
  return jsonMutation<CookLogSocial>(
    `/api/realisations/${logId}/reaction`,
    'PUT',
    { kind } satisfies UpdateReactionRequest,
  );
}

/** GET /api/activite (US-68) — le flux familial et le nombre de nouveautés, dans le périmètre visible. */
export function listActivity(signal?: AbortSignal): Promise<ListActivityResponse> {
  return fetch('/api/activite', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListActivityResponse>(response));
}

/** POST /api/activite/vu — « j'ai vu ». Remet le repère à maintenant, pour ce membre seulement. */
export function markActivitySeen(): Promise<ListActivityResponse> {
  return jsonMutation<ListActivityResponse>('/api/activite/vu', 'POST', {});
}

/** GET /api/recipes/:id/propositions (US-67) — le fil, ouvertes en tête. Visible de qui voit la recette. */
export function listSuggestions(recipeId: string, signal?: AbortSignal): Promise<ListSuggestionsResponse> {
  return fetch(`/api/recipes/${recipeId}/propositions`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListSuggestionsResponse>(response));
}

/** POST /api/recipes/:id/propositions — proposer un changement décrit en données. */
export function createSuggestion(recipeId: string, body: CreateSuggestionRequest): Promise<ListSuggestionsResponse> {
  return jsonMutation<ListSuggestionsResponse>(`/api/recipes/${recipeId}/propositions`, 'POST', body);
}

/** POST …/propositions/:id/reponse — accepter, refuser (propriétaire) ou retirer (auteur). En bloc. */
export function resolveSuggestion(
  recipeId: string,
  suggestionId: string,
  decision: ResolveSuggestionRequest['decision'],
): Promise<ListSuggestionsResponse> {
  return jsonMutation<ListSuggestionsResponse>(
    `/api/recipes/${recipeId}/propositions/${suggestionId}/reponse`,
    'POST',
    { decision } satisfies ResolveSuggestionRequest,
  );
}

/** POST /api/journal/partage (US-65) — publier son passé d'un geste. Ce qui n'est pas à soi est ignoré. */
export function shareCookLogs(ids: string[], shared: boolean): Promise<ShareCookLogsResponse> {
  return jsonMutation<ShareCookLogsResponse>(
    '/api/journal/partage',
    'POST',
    { ids, shared } satisfies ShareCookLogsRequest,
  );
}

export function updateRecipeRangement(
  id: string,
  status: RecipeStatus | null,
): Promise<UpdateRecipeRangementResponse> {
  return jsonMutation<UpdateRecipeRangementResponse>(
    `/api/recipes/${id}/rangement`,
    'PUT',
    { status } satisfies UpdateRecipeRangementRequest,
  );
}

/** PUT /api/recipes/:id/tags — remplace les étiquettes manuelles que ce membre a posées, par nom. Les calculées ne sont pas touchées. */
export function updateRecipeTags(id: string, names: string[]): Promise<UpdateRecipeTagsResponse> {
  return jsonMutation<UpdateRecipeTagsResponse>(
    `/api/recipes/${id}/tags`,
    'PUT',
    { names } satisfies UpdateRecipeTagsRequest,
  );
}

export function listCollections(signal?: AbortSignal): Promise<ListCollectionsResponse> {
  return fetch('/api/collections', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListCollectionsResponse>(response));
}

export function createCollection(body: UpsertCollectionRequest): Promise<UpsertCollectionResponse> {
  return jsonMutation<UpsertCollectionResponse>('/api/collections', 'POST', body);
}

export function updateCollection(id: string, body: UpsertCollectionRequest): Promise<UpsertCollectionResponse> {
  return jsonMutation<UpsertCollectionResponse>(`/api/collections/${id}`, 'PATCH', body);
}

/** PUT /api/collections/ordre (US-63) — l'ordre, enfin modifiable. Personnel : les identifiants d'autrui sont ignorés. */
export function reorderCollections(ids: string[]): Promise<ListCollectionsResponse> {
  return jsonMutation<ListCollectionsResponse>('/api/collections/ordre', 'PUT', { ids });
}

/** GET /api/recipes/:id/collections (US-63) — « cette recette apparaît dans : Desserts, Le dimanche ». */
export function listRecipeCollections(recipeId: string, signal?: AbortSignal): Promise<RecipeCollectionsResponse> {
  return fetch(`/api/recipes/${recipeId}/collections`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<RecipeCollectionsResponse>(response));
}

export function deleteCollection(id: string): Promise<void> {
  return deleteOrThrow(`/api/collections/${id}`);
}

/** POST /api/recipes — saisie manuelle (US-01). Mis en file hors ligne (US-12) : « l'enregistrement fonctionne hors connexion » (AC US-01). */
export function createRecipe(body: CreateRecipeRequest): Promise<CreateRecipeResponse> {
  return jsonMutation<CreateRecipeResponse>('/api/recipes', 'POST', body);
}

/** GET /api/recipes/:id — fiche recette (US-06). */
export function getRecipe(id: string, signal?: AbortSignal): Promise<GetRecipeResponse> {
  return fetch(`/api/recipes/${id}`, { signal }).then((response) => readJsonOrThrow<GetRecipeResponse>(response));
}

/** PATCH /api/recipes/:id — édition sur place, un ou plusieurs champs à la fois (US-06). Mis en file hors ligne (US-12). */
export function updateRecipe(id: string, body: UpdateRecipeRequest): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}`, 'PATCH', body);
}

/**
 * POST /api/recipes/:id/archive et POST /api/recipes/:id/restore (refonte
 * UX/UI, archivage) : bascule `recipe.archived`, jamais de suppression (§3
 * CLAUDE.md « on archive, on ne supprime jamais »). Mis en file hors ligne
 * (US-12), même principe que les autres écritures de la fiche recette.
 */
export function archiveRecipe(id: string): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/archive`, 'POST', {});
}

/** POST /api/recipes/:id/restore — réaffiche une recette archivée dans la bibliothèque. Voir `archiveRecipe`. */
export function restoreRecipe(id: string): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/restore`, 'POST', {});
}

/**
 * GET /api/recipes/:id/suppression (US-83) — **ce que la suppression emporte,
 * avant de l'emporter** (D16).
 *
 * Distincte de la fiche : celle-ci ne dit rien des réalisations des autres, des
 * commentaires, des liens publics ni des entrées du plan, et l'écran de
 * confirmation ne doit pas les deviner. Répond 404 dans tous les cas de refus,
 * y compris « pas encore archivée » — le seul chemin qui mène ici passe par
 * l'écran des archivées.
 */
export function apercuSuppression(id: string, signal?: AbortSignal): Promise<ApercuSuppression> {
  return fetch(`/api/recipes/${id}/suppression`, signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ApercuSuppression>(response));
}

/**
 * DELETE /api/recipes/:id (US-83) — la suppression définitive.
 *
 * Passe par `deleteOrThrow`, donc **jamais mise en file hors ligne** : une
 * suppression rejouée à la reconnexion viserait un objet dont on ne sait plus
 * rien, et promettre un effacement différé serait pire que d'échouer
 * franchement. C'est déjà la règle des autres suppressions du produit ; celle-ci
 * n'y fait pas exception, elle en est le cas le plus net.
 */
export function deleteRecipe(id: string): Promise<void> {
  return deleteOrThrow(`/api/recipes/${id}`);
}

/** PUT /api/recipes/:id/ingredients — remplace l'intégralité des ingrédients (US-06). Mis en file hors ligne (US-12). */
export function updateIngredients(id: string, body: UpdateIngredientsRequest): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/ingredients`, 'PUT', body);
}

/** PUT /api/recipes/:id/steps — remplace l'intégralité des étapes (US-06). Mis en file hors ligne (US-12). */
export function updateSteps(id: string, body: UpdateStepsRequest): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/steps`, 'PUT', body);
}

/**
 * PUT /api/recipes/:id/steps/ordre (US-77) — l'ordre des étapes, par un geste.
 *
 * La liste entière, dans l'ordre voulu, et non « monte l'étape 3 » : deux
 * onglets ouverts, le dernier ordre envoyé gagne entièrement. `group_label`
 * accompagne l'identifiant parce qu'une étape qui change de place peut changer
 * de phase — c'est `deplacerLigne` (`@mlc/shared`) qui l'a décidé, ici comme
 * côté serveur. Mise en file hors ligne (US-12) comme les autres écritures JSON.
 */
export function reorderSteps(id: string, steps: StepOrderInput[]): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/steps/ordre`, 'PUT', { steps });
}

/**
 * PUT /api/recipes/:id/step-ingredients — correction manuelle de
 * l'appariement automatique étape ↔ ingrédient (US-08). Mis en file hors
 * ligne (US-12) comme les autres écritures JSON de la fiche.
 */
export function updateStepIngredients(id: string, body: UpdateStepIngredientsRequest): Promise<UpdateRecipeResponse> {
  return jsonMutation<UpdateRecipeResponse>(`/api/recipes/${id}/step-ingredients`, 'PUT', body);
}

/** POST /api/recipes/:id/photo — photo principale, multipart, un seul fichier (US-06). */
export function uploadPhoto(id: string, file: File): Promise<UploadPhotoResponse> {
  const form = new FormData();
  form.append('photo', file);
  return fetch(`/api/recipes/${id}/photo`, { method: 'POST', body: form }).then((response) =>
    readJsonOrThrow<UploadPhotoResponse>(response),
  );
}

/** POST /api/recipes/:id/cook-log — enregistrer une réalisation (US-10). Mis en file hors ligne (US-12) : « la cuisine est l'endroit où le réseau tombe » (§1 CLAUDE.md). */
export function createCookLog(id: string, body: CreateCookLogRequest): Promise<CreateCookLogResponse> {
  return jsonMutation<CreateCookLogResponse>(`/api/recipes/${id}/cook-log`, 'POST', body);
}

/** POST /api/recipes/:id/cook-log/:logId/photo — photo de la réalisation, même principe que la photo de recette (US-10). */
export function uploadCookLogPhoto(id: string, logId: string, file: File): Promise<UploadCookLogPhotoResponse> {
  const form = new FormData();
  form.append('photo', file);
  return fetch(`/api/recipes/${id}/cook-log/${logId}/photo`, { method: 'POST', body: form }).then((response) =>
    readJsonOrThrow<UploadCookLogPhotoResponse>(response),
  );
}

/**
 * POST /api/import/url — import depuis une URL (US-02). Ne persiste rien : le
 * résultat alimente l'écran d'édition existant (`RecipeForm`, via `ImportUrlForm`).
 */
export function importFromUrl(body: ImportUrlRequest): Promise<ImportUrlResponse> {
  return fetch('/api/import/url', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => readJsonOrThrow<ImportUrlResponse>(response));
}

/**
 * POST /api/import/text — collage de texte libre (US-03). Traitement
 * entièrement local côté serveur, aucun accès réseau ni appel LLM.
 */
export function importFromText(body: ImportTextRequest): Promise<ImportTextResponse> {
  return fetch('/api/import/text', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => readJsonOrThrow<ImportTextResponse>(response));
}

/**
 * POST /api/import/photo — lecture d'une page photographiée (US-32, §1).
 * Un seul appel multimodal côté serveur ; rien n'est persisté, le résultat
 * passe par le même écran de validation que les autres imports.
 */
export function importFromPhotos(body: ImportPhotoRequest): Promise<ImportPhotoResponse> {
  return fetch('/api/import/photo', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => readJsonOrThrow<ImportPhotoResponse>(response));
}

/**
 * Le sas (US-71) — ce qu'on a mis de côté et pas encore gardé.
 *
 * Aucune fonction de capture ici : dans cette story, les entrées arrivent par
 * l'API (le raccourci est US-72). L'écran ne fait que lire, garder et jeter.
 */

/** GET /api/import/drafts — le sas du membre connecté, la plus récente en tête. */
export function listImportDrafts(signal?: AbortSignal): Promise<ListImportDraftsResponse> {
  return fetch('/api/import/drafts', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListImportDraftsResponse>(response));
}

/**
 * POST /api/import/draft/:id/garder — **une tape**.
 *
 * Sans corps : le contenu capturé entre tel quel. Avec corps : le contenu relu
 * sur l'écran de validation le remplace. Dans les deux cas la photo suit et
 * l'entrée disparaît — c'est ce qui doit rester indivisible.
 */
export function garderImportDraft(
  id: string,
  corrige?: GarderImportDraftRequest,
): Promise<GarderImportDraftResponse> {
  // `jsonMutation` mettrait en file hors ligne (US-12), ce qui n'a pas de sens
  // ici : l'entrée à garder vit sur le serveur, et rejouer la demande plus tard
  // sur une entrée peut-être périmée promettrait un effet qui n'aurait pas
  // lieu. Même raisonnement que `deleteOrThrow`.
  return fetch(`/api/import/draft/${encodeURIComponent(id)}/garder`, {
    method: 'POST',
    ...(corrige === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(corrige) }),
  }).then((response) => readJsonOrThrow<GarderImportDraftResponse>(response));
}

/** DELETE /api/import/draft/:id — jeter. L'entrée part, sa photo avec elle. */
export function jeterImportDraft(id: string): Promise<void> {
  return deleteOrThrow(`/api/import/draft/${encodeURIComponent(id)}`);
}

/**
 * PUT /api/import/draft/:id/propositions — **retirer une puce** (US-73).
 *
 * Le corps porte ce qui reste, pas ce qu'on enlève, et le serveur n'en retient
 * que ce qu'il avait lui-même proposé. L'écran, lui, a déjà recalculé sa ligne
 * « Rejoindra » sans attendre : cet appel ne fait que persister le geste, pour
 * que « Garder » entre plus tard une recette sans la valeur retirée.
 *
 * Comme « Garder » et « Jeter », il ne passe pas par la file hors ligne : la
 * proposition qu'il modifie vit sur le serveur, et rejouer la demande sur une
 * entrée peut-être périmée promettrait un effet qui n'aurait pas lieu.
 */
export function majPropositions(id: string, restant: MajPropositionsRequest): Promise<MajPropositionsResponse> {
  return fetch(`/api/import/draft/${encodeURIComponent(id)}/propositions`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(restant),
  }).then((response) => readJsonOrThrow<MajPropositionsResponse>(response));
}

// ----------------------------------------------------------------------------
// Le raccourci « Ajouter à Mijoté » (US-72)
// ----------------------------------------------------------------------------

/** GET /api/raccourcis — les siens. Le jeton n'y est pas : il n'existe en clair qu'une fois. */
export function listShortcutTokens(signal?: AbortSignal): Promise<ListShortcutTokensResponse> {
  return fetch('/api/raccourcis', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<ListShortcutTokensResponse>(response));
}

/**
 * POST /api/raccourcis — créer un jeton.
 *
 * **Volontairement hors de `jsonMutation`**, donc hors de la file hors ligne
 * (US-12), pour la même raison que les réglages d'US-69 : la réponse contient
 * un secret, et la file conserve les requêtes en attente dans `localStorage`,
 * lisible par tout le JavaScript de l'origine. Installer un raccourci est un
 * geste qu'on fait assis, avec du réseau.
 */
export function createShortcutToken(label: string): Promise<CreateShortcutTokenResponse> {
  return fetch('/api/raccourcis', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ label } satisfies CreateShortcutTokenRequest),
  }).then((response) => readJsonOrThrow<CreateShortcutTokenResponse>(response));
}

/** DELETE /api/raccourcis/:id — révoquer. Le raccourci reçoit un 401 lisible au prochain envoi. */
export function revokeShortcutToken(id: string): Promise<void> {
  return deleteOrThrow(`/api/raccourcis/${encodeURIComponent(id)}`);
}

/**
 * GET /api/ia — l'assistance IA est-elle configurée ? Sert à masquer les
 * entrées qui en dépendent (import photo, US-32) plutôt qu'à laisser
 * l'utilisateur découvrir l'absence de clé au moment de l'échec (§1 :
 * « toute fonction IA se dégrade proprement si la clé est absente »).
 */
export function readAiAvailability(signal?: AbortSignal): Promise<{ available: boolean }> {
  return fetch('/api/ia', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<{ available: boolean }>(response));
}

/**
 * Signale que la configuration de l'IA vient de changer (US-69).
 *
 * Même mécanisme que `mlc:unauthenticated` : n'importe quel écran peut poser
 * une clé, et c'est `App` qui tient l'état « l'import photo est-il proposé ? ».
 * Sans cet événement, l'entrée n'apparaîtrait qu'au rechargement suivant —
 * c'est-à-dire que « sans redémarrage » serait vrai du serveur et faux de
 * l'écran, ce qui revient au même pour qui vient de coller sa clé.
 */
function signalerChangementIa(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('mlc:ia'));
}

// ----------------------------------------------------------------------------
// Réglages de la maison (US-69)
// ----------------------------------------------------------------------------

/** GET /api/reglages — 404 pour qui n'est pas propriétaire, et c'est voulu (§1 « Visibilité »). */
export function readInstanceSettings(signal?: AbortSignal): Promise<InstanceSettingsResponse> {
  return fetch('/api/reglages', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<InstanceSettingsResponse>(response));
}

/**
 * PATCH /api/reglages.
 *
 * **Volontairement hors de `jsonMutation`**, donc hors de la file hors ligne
 * (US-12) : celle-ci conserve le corps de la requête dans `localStorage` en
 * attendant la reconnexion. Une clé d'API y séjournerait, lisible par tout le
 * JavaScript de l'origine, alors même que la purge d'US-47 ne vide ce stockage
 * qu'à la déconnexion. Régler la maison est un geste qu'on fait assis, avec du
 * réseau : sans réseau, il échoue franchement.
 */
export async function updateInstanceSettings(
  body: UpdateInstanceSettingsRequest,
): Promise<InstanceSettingsResponse> {
  const response = await fetch('/api/reglages', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const etat = await readJsonOrThrow<InstanceSettingsResponse>(response);
  signalerChangementIa();
  return etat;
}

/** POST /api/reglages/ia/verification — un appel réel, minimal, à la demande. */
export function checkAiKey(): Promise<CheckAiKeyResponse> {
  return fetch('/api/reglages/ia/verification', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  }).then((response) => readJsonOrThrow<CheckAiKeyResponse>(response));
}

// ----------------------------------------------------------------------------
// Export et restauration (US-12)
// ----------------------------------------------------------------------------

/**
 * GET /api/export — utilisé tel quel comme `href` d'un lien de téléchargement
 * (App.tsx) : le serveur répond avec `content-disposition: attachment`, pas
 * besoin de passer par `fetch` et un `Blob` côté client pour déclencher le
 * téléchargement du navigateur.
 */
export const EXPORT_ARCHIVE_URL = '/api/export';

/** GET /api/recipes/:id/export.pdf — même principe que `EXPORT_ARCHIVE_URL`, un lien direct suffit. */
export function recipePdfUrl(id: string): string {
  return `/api/recipes/${id}/export.pdf`;
}

/**
 * POST /api/import — restauration depuis une archive (US-12). Jamais mis en
 * file hors ligne (contrairement aux écritures ci-dessus) : restaurer une
 * sauvegarde est une action explicite et ponctuelle, pas une saisie qu'on
 * s'attend à pouvoir faire au fond d'une cuisine sans réseau.
 */
export function importArchive(file: File): Promise<ImportResponse> {
  const form = new FormData();
  form.append('archive', file);
  return fetch('/api/import', { method: 'POST', body: form }).then((response) => readJsonOrThrow<ImportResponse>(response));
}

/**
 * POST /api/session — connexion (US-42 ; auparavant mot de passe de foyer,
 * US-19). Le mot de passe n'est jamais mémorisé côté client : seul le cookie
 * signé `HttpOnly` posé par le serveur persiste, hors de portée du JavaScript
 * de la page.
 */
export function openSession(email: string, password: string): Promise<SessionState> {
  return fetch('/api/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  }).then((response) => readJsonOrThrow<SessionState>(response));
}

/** GET /api/session — qui est connecté, et faut-il encore créer le premier compte (US-42). */
export function readSession(): Promise<SessionState> {
  return fetch('/api/session').then((response) => readJsonOrThrow<SessionState>(response));
}

/**
 * POST /api/membres/premier — création du premier compte (US-42).
 *
 * La seule route servie tant que la base n'a aucun membre, et elle disparaît
 * dès qu'il en existe un : la laisser ouverte reviendrait à laisser n'importe
 * qui se créer un propriétaire.
 */
export function createFirstMember(
  input: { first_name: string; last_name?: string | null; email: string; password: string },
): Promise<PublicMember> {
  return fetch('/api/membres/premier', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJsonOrThrow<PublicMember>(response));
}

/**
 * POST /api/recipes/visibilite — bascule en lot (US-49).
 *
 * Après la migration, tout est privé et il faut décider recette par recette :
 * sans geste de masse, l'étape est repoussée et le livre familial reste vide
 * le jour du lancement.
 */
export function setRecipesVisibility(ids: string[], visibility: RecipeVisibility): Promise<{ updated: number }> {
  return fetch('/api/recipes/visibilite', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids, visibility }),
  }).then((response) => readJsonOrThrow<{ updated: number }>(response));
}

/** POST /api/recipes/:id/copie — dupliquer la recette d'un autre (US-49). */
export function copyRecipe(id: string): Promise<GetRecipeResponse> {
  return fetch(`/api/recipes/${id}/copie`, { method: 'POST' })
    .then((response) => readJsonOrThrow<GetRecipeResponse>(response));
}

export interface PublicDeviceSession {
  id: string;
  label: string;
  created_by_name: string | null;
  created_at: string;
  last_seen_at: string | null;
}

/** GET /api/appareils — les appareils en mode partagé (US-50). */
export function listDevices(): Promise<PublicDeviceSession[]> {
  return fetch('/api/appareils').then((response) => readJsonOrThrow<PublicDeviceSession[]>(response));
}

/**
 * POST /api/appareils — active le mode partagé **sur cet appareil-ci** (US-50).
 *
 * Le jeton est posé en cookie et n'est jamais renvoyé : il n'y a rien à copier
 * ni à transmettre. Configurer une autre tablette se fait depuis cette
 * tablette-là.
 */
export function enableDeviceMode(label: string): Promise<PublicDeviceSession> {
  return fetch('/api/appareils', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ label }),
  }).then((response) => readJsonOrThrow<PublicDeviceSession>(response));
}

/** DELETE /api/appareils/:id — coupe l'appareil, immédiatement (US-50). */
export function revokeDeviceMode(id: string): Promise<void> {
  return deleteOrThrow(`/api/appareils/${id}`);
}

/**
 * POST /api/motdepasse/oubli — demande un lien de réinitialisation (US-53).
 *
 * Ne passe **pas** par la file hors ligne : une demande rejouée trois jours
 * plus tard enverrait un message que plus personne n'attend, et le seul geste
 * utile hors connexion est d'attendre le réseau.
 *
 * La réponse est identique que l'adresse existe ou non — c'est voulu, et le
 * client n'a donc rien à en déduire.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch('/api/motdepasse/oubli', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) await throwApiError(response);
}

/**
 * POST /api/accueil/vu — « j'ai vu l'écran des premiers pas » (US-104).
 *
 * Idempotente côté serveur : la date qui compte est celle de la première fois.
 */
export async function marquerAccueilVu(): Promise<void> {
  const response = await fetch('/api/accueil/vu', { method: 'POST' });
  if (!response.ok) await throwApiError(response);
}

/**
 * POST /api/invitations — invite quelqu'un (US-103). Propriétaire seul.
 *
 * La réponse porte le lien **une seule fois** : il n'est ni relu ni relisible
 * ensuite, et `listInvitations` ne le renvoie jamais. L'écran l'affiche donc
 * tant qu'il est là, et propose de le copier.
 */
export function createInvitation(
  input: { first_name: string; last_name?: string | null; email: string; role: MemberRole },
): Promise<CreateInvitationResponse> {
  return fetch('/api/invitations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJsonOrThrow<CreateInvitationResponse>(response));
}

/** GET /api/invitations — les invitations en attente (US-103). Propriétaire seul. */
export function listInvitations(): Promise<PublicInvitation[]> {
  return fetch('/api/invitations').then((response) => readJsonOrThrow<PublicInvitation[]>(response));
}

/**
 * POST /api/invitations/:id/annulation — annule une invitation (US-103).
 *
 * `POST` et non `DELETE` : on révoque, on ne supprime pas (§2), et la file
 * hors ligne ne rejoue de toute façon pas les `DELETE`.
 */
export async function cancelInvitation(id: string): Promise<void> {
  const response = await fetch(`/api/invitations/${encodeURIComponent(id)}/annulation`, { method: 'POST' });
  if (!response.ok) await throwApiError(response);
}

/**
 * GET /api/invitation/:token — ce que l'invité voit avant de choisir son mot
 * de passe (US-103). Publique : il n'a pas encore de compte.
 */
export function readInvitation(token: string): Promise<InvitationInfo> {
  return fetch(`/api/invitation/${encodeURIComponent(token)}`)
    .then((response) => readJsonOrThrow<InvitationInfo>(response));
}

/** POST /api/invitation/:token — crée le compte et ouvre la session (US-103). */
export function acceptInvitation(
  token: string,
  input: { password: string; first_name?: string; last_name?: string | null },
): Promise<PublicMember> {
  return fetch(`/api/invitation/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJsonOrThrow<PublicMember>(response));
}

/** POST /api/motdepasse/reinitialisation — pose le nouveau mot de passe (US-53). */
export async function resetPassword(jeton: string, password: string): Promise<void> {
  const response = await fetch('/api/motdepasse/reinitialisation', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jeton, password }),
  });
  if (!response.ok) await throwApiError(response);
}

/**
 * GET /api/cuisines — suggestions de saisie (US-55).
 *
 * Filtrée côté serveur par la clause de visibilité : une cuisine qui n'existe
 * que dans une recette privée d'un autre membre n'en sort jamais.
 */
export function listCuisines(signal?: AbortSignal): Promise<string[]> {
  return fetch('/api/cuisines', signal === undefined ? {} : { signal })
    .then((response) => readJsonOrThrow<unknown>(response))
    // Une liste de suggestions ne doit **jamais** faire tomber l'écran qui la
    // porte : elle n'est pas la donnée, elle aide à la saisir. Le filtrage de
    // forme est ici et non chez les deux appelants, pour qu'ils n'aient pas
    // chacun à s'en souvenir.
    .then((valeur) => (Array.isArray(valeur) ? valeur.filter((nom): nom is string => typeof nom === 'string') : []));
}

/** GET /api/membres — les personnes avec qui on partage le livre (US-42). */
export function listMembers(): Promise<PublicMember[]> {
  return fetch('/api/membres').then((response) => readJsonOrThrow<PublicMember[]>(response));
}

/** POST /api/membres — réservé au propriétaire (US-42). */
export function createMember(
  input: {
    first_name: string;
    last_name?: string | null;
    email: string;
    password: string;
    role: MemberRole;
  },
): Promise<PublicMember> {
  return fetch('/api/membres', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJsonOrThrow<PublicMember>(response));
}

/** PATCH /api/membres/:id — identité et mot de passe pour soi, rôle et désactivation pour le propriétaire. */
export function updateMember(
  id: string,
  input: {
    first_name?: string;
    last_name?: string | null;
    email?: string;
    password?: string;
    role?: MemberRole;
    disabled?: boolean;
  },
): Promise<PublicMember> {
  return fetch(`/api/membres/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJsonOrThrow<PublicMember>(response));
}

/** POST /api/membres/:id/deconnexion — « déconnecter mes autres appareils » (US-42, cas du téléphone perdu). */
export function revokeMemberSessions(id: string): Promise<PublicMember> {
  return fetch(`/api/membres/${id}/deconnexion`, { method: 'POST' })
    .then((response) => readJsonOrThrow<PublicMember>(response));
}

/**
 * GET /api/health — version servie (US-38). Route publique, mais l'empreinte
 * git et la date de construction ne sont renvoyées qu'à une requête portant
 * une session (`server/src/routes/health.ts`).
 */
export function fetchHealth(): Promise<HealthResponse> {
  return fetch('/api/health').then((response) => readJsonOrThrow<HealthResponse>(response));
}

/** DELETE /api/session — déconnexion (US-19, US-42). */
export function closeSession(): Promise<void> {
  return fetch('/api/session', { method: 'DELETE' }).then(() => undefined);
}
